import React, { useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import api from '../services/api';
import { useI18n } from '../i18n/I18nContext';
import './Login.css';
import { useUser } from '../context/UserContext';
import Logo from './common/Logo';

function Login() {
  const { t } = useI18n();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [validationErrors, setValidationErrors] = useState({});
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isAuthenticated, userType } = useUser();

  const from = location.state?.from?.pathname || (userType === 'admin' ? '/admin' : '/dashboard');

  React.useEffect(() => {
    if (isAuthenticated && window.location.pathname !== from) {
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, from]);

  const validateForm = () => {
    const errors = {};
    if (!formData.email) {
      errors.email = t('auth.emailRequired');
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = t('auth.emailInvalid');
    }
    if (!formData.password) {
      errors.password = t('auth.passwordRequired');
    } else if (formData.password.length < 6) {
      errors.password = t('auth.passwordMin');
    }
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (validationErrors[name]) {
      setValidationErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!validateForm()) return;
    try {
      setLoading(true);
      const result = await login(formData.email, formData.password);
      if (!result.success) {
        setError(result.error || t('auth.loginFailed'));
      }
      // Success - UserContext will update and redirect will happen via useEffect
    } catch (err) {
      setError(err.response?.data?.message || t('auth.loginFailed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-left-panel">
        <div className="left-content">
          <Logo variant="auth" className="left-logo" />

          
        </div>
      </div>

      <div className="login-right-panel">
        <div className="login-card">
          <h2 className="login-title">{t('auth.signIn')}</h2>
          <p className="login-subtitle">{t('auth.accessYourAccount')}</p>
          {error && (
            <div className="alert alert-danger" role="alert">
              <span>{error}</span>
            </div>
          )}
          <form onSubmit={handleSubmit} className="login-form" autoComplete="off">
            <div className="form-group">
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="form-input"
                placeholder=" "
                autoComplete="username"
                required
              />
              <label htmlFor="email" className="form-label">{t('auth.email')}</label>
              {validationErrors.email && (
                <div className="error-message">{validationErrors.email}</div>
              )}
            </div>
            <div className="form-group">
              <input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                className="form-input"
                placeholder=" "
                autoComplete="current-password"
                required
              />
              <label htmlFor="password" className="form-label">{t('auth.password')}</label>
              {validationErrors.password && (
                <div className="error-message">{validationErrors.password}</div>
              )}
            </div>
            <div className="form-options">
              <div className="remember-me">
                <input
                  type="checkbox"
                  id="rememberMe"
                  name="rememberMe"
                  checked={formData.rememberMe}
                  onChange={handleChange}
                  className="checkbox-input"
                />
                <label htmlFor="rememberMe" className="checkbox-label">
                  {t('auth.rememberMe')}
                </label>
              </div>
              {/*
                <Link to="/forgot-password" className="forgot-link">
                  Forgot password?
                </Link>
              */}
            </div>
            <button
              type="submit"
              className="login-button"
              disabled={loading}
            >
              {loading ? t('auth.signingIn') : t('auth.signIn')}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

export default Login;